// FamilyTree1.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Card, Tooltip, CircularProgress } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import Icon from "@mui/material/Icon";
import MaleImage from "../../../assets/images/male.png";
import FemaleImage from "../../../assets/images/female.png";
import RelationshipChecker from "./RelationshipChecker";
import ExistingMemberLinkDialog from "./ExistingMemberLinkDialog";

/* ---------- constants ---------- */
const NODE_W = 150;
const NODE_H = 120;
const COUPLE_GAP = 36;
const H_GAP = 60;
const V_GAP = 270; // increased by 50px for more space between generations
const MARRIAGE_DOT_R = 6;
const CHILD_GROUP_GAP = 70;

const BASE_AVATAR_SIZE = 72;
const AVATAR_TOP = 12;
const HIGHLIGHT_HALO_COLOR = "#ffffff";
const HIGHLIGHT_CORE_WIDTH = 6;
const HIGHLIGHT_HALO_WIDTH = 10;
const SELF_BADGE_LABEL = "You";
const SELF_BADGE_BG = "#dc2626";
const SELF_BADGE_TEXT_COLOR = "#fee2e2";
const SELF_OVERLAY_GRADIENT_ID = "selfHighlightOverlay";
const SELF_FLARE_GRADIENT_ID = "selfHighlightFlare";
const SELF_FLARE_ANIMATION_DURATION = 2;
const SELF_GLOW_FILTER_ID = "selfOuterGlowRing";
const SELF_GLOW_BLUR_ID = "selfOuterGlowBlur";

const normalizeGender = (value) => (value ?? "").toString().trim().toLowerCase();

const getBorderColor = (person) => {
  const status = person.aliveStatus;
  if (status === "Alive" || status === "alive" || status === null) return "#4CAF50";
  return "#F44336";
};

const userId = JSON.parse(localStorage.getItem('userId') || '{}').value;

const isMyselfRecord = (person) => {
  if (!person || userId == null) return false;
  const linkedId = person.linkedUserId ?? person.linked_user_id_c ?? person.linked_user_id ?? person.linked_userid;
  if (linkedId != null) return String(linkedId) === String(userId);
  return String(person.id) === String(userId);
};

function computeNameLayout(name) {
  const raw = (name || '').trim();
  if (!raw) {
    return { lines: [""], fontSize: 13, avatarSize: BASE_AVATAR_SIZE };
  }
  const words = raw.split(/\s+/);
  const mid = Math.ceil(words.length / 2);
  const line1 = words.slice(0, mid).join(" ");
  const line2 = words.slice(mid).join(" ");
  const totalLen = raw.length;
  let fontSize = 13;
  let avatarSize = BASE_AVATAR_SIZE;
  if (totalLen > 18) fontSize = 12;
  if (totalLen > 24) { fontSize = 11; avatarSize = BASE_AVATAR_SIZE - 6; }
  if (totalLen > 32) { fontSize = 10; avatarSize = BASE_AVATAR_SIZE - 10; }
  const lines = line2 ? [line1, line2] : [line1];
  return { lines, fontSize, avatarSize };
}

function buildPersonMap(members) {
  const m = new Map();
  (members || []).forEach((p) => m.set(p.id, p));
  return m;
}

function buildNodes(members) {
  const personMap = buildPersonMap(members);
  const personSeen = new Set();
  const nodes = new Map();
  const personToNode = new Map();
  const parentsMap = new Map();
  const parentChildMap = new Map();

  for (const p of members) {
    if (personSeen.has(p.id)) continue;
    const spouseId = p.spouseId;
    if (spouseId && personMap.has(spouseId)) {
      const a = p;
      const b = personMap.get(spouseId);
      const nodeId = a.id < b.id ? `${a.id}_${b.id}` : `${b.id}_${a.id}`;
      let personsArr = [a, b];
      const gA = normalizeGender(a.gender);
      const gB = normalizeGender(b.gender);
      if (gA !== gB) {
        if (gA === "male") personsArr = [a, b];
        else if (gB === "male") personsArr = [b, a];
      }
      nodes.set(nodeId, { id: nodeId, type: "couple", persons: personsArr, children: new Set() });
      personToNode.set(a.id, nodeId);
      personToNode.set(b.id, nodeId);
      personSeen.add(a.id);
      personSeen.add(b.id);
    } else {
      const nodeId = p.id;
      nodes.set(nodeId, { id: nodeId, type: "single", persons: [p], children: new Set() });
      personToNode.set(p.id, nodeId);
      personSeen.add(p.id);
    }
  }

  for (const p of members) {
    const childNodeId = personToNode.get(p.id);
    const parentPersonId = p.parentId;
    if (!parentPersonId) continue;
    const parentNodeId = personToNode.get(parentPersonId);
    if (!parentNodeId) continue;
    if (parentNodeId === childNodeId) continue;
    const parentNode = nodes.get(parentNodeId);
    if (!parentNode) continue;
    parentNode.children.add(childNodeId);
    if (!parentsMap.has(childNodeId)) parentsMap.set(childNodeId, new Set());
    parentsMap.get(childNodeId).add(parentNodeId);

    const linkKey = `${parentNodeId}::${childNodeId}`;
    if (!parentChildMap.has(linkKey)) parentChildMap.set(linkKey, new Set());
    parentChildMap.get(linkKey).add(parentPersonId);
  }

  return { nodes, personToNode, parentsMap, parentChildMap };
}

function buildForest(nodes) {
  const childSet = new Set();
  for (const node of nodes.values()) {
    for (const c of node.children) childSet.add(c);
  }
  const allIds = Array.from(nodes.keys());
  const roots = allIds.filter((id) => !childSet.has(id));
  return { roots, nodes };
}

function resolveMutualSpouse(person, personMap) {
  if (!person || !person.spouseId) return null;
  const spouse = personMap.get(person.spouseId);
  if (!spouse || spouse.spouseId !== person.id) return null;
  return spouse;
}

/* ---------- computeLayout (kept similar to your earlier algorithm) ---------- */
function computeLayout(nodesMap, roots, personMap, personToNode, parentsMap, parentChildMap) {
  const nodeWidth = (node) => (node.type === "single" ? NODE_W : NODE_W * 2 + COUPLE_GAP);
  const positions = new Map();

  const allNodeIds = Array.from(nodesMap.keys());
  const depthMemo = new Map();
  const visiting = new Set();

  const computeDepthValue = (nodeId) => {
    if (depthMemo.has(nodeId)) return depthMemo.get(nodeId);
    if (visiting.has(nodeId)) {
      console.warn("FamilyTreeUi: detected ancestry cycle", nodeId);
      depthMemo.set(nodeId, 0);
      return 0;
    }
    visiting.add(nodeId);
    const parentSet = parentsMap.get(nodeId);
    let depth = 0;
    if (parentSet && parentSet.size) {
      let maxParentDepth = 0;
      for (const pid of parentSet) {
        const parentDepth = computeDepthValue(pid);
        if (parentDepth + 1 > maxParentDepth) maxParentDepth = parentDepth + 1;
      }
      depth = maxParentDepth;
    }
    depthMemo.set(nodeId, depth);
    visiting.delete(nodeId);
    return depth;
  };

  for (const nid of allNodeIds) computeDepthValue(nid);

  // Drop older-generation spouses down to the younger partner's level without touching their parents.
  const pendingDrops = [];
  for (const node of nodesMap.values()) {
    if (!node || !node.persons || !node.persons.length) continue;
    const currentDepth = depthMemo.get(node.id) ?? 0;
    let requiredDepth = currentDepth;
    for (const person of node.persons) {
      if (!person?.parentId) continue;
      const parentNodeId = personToNode.get(person.parentId);
      if (!parentNodeId || parentNodeId === node.id) continue;
      const parentDepth = depthMemo.get(parentNodeId);
      if (parentDepth === undefined) continue;
      requiredDepth = Math.max(requiredDepth, parentDepth + 1);
    }
    if (requiredDepth > currentDepth) {
      pendingDrops.push({ nodeId: node.id, targetDepth: requiredDepth });
    }
  }

  const shiftSubtree = (startId, delta) => {
    if (delta <= 0) return;
    const visited = new Set();
    const q = [startId];
    while (q.length) {
      const nid = q.shift();
      if (visited.has(nid)) continue;
      visited.add(nid);
      depthMemo.set(nid, (depthMemo.get(nid) ?? 0) + delta);
      const node = nodesMap.get(nid);
      if (!node || !node.children) continue;
      for (const childId of node.children) q.push(childId);
    }
  };

  for (const drop of pendingDrops) {
    const existing = depthMemo.get(drop.nodeId) ?? 0;
    if (drop.targetDepth > existing) {
      shiftSubtree(drop.nodeId, drop.targetDepth - existing);
    }
  }

  const minDepth = Math.min(...depthMemo.values());
  if (minDepth > 0) {
    for (const [nid, depth] of depthMemo.entries()) {
      depthMemo.set(nid, depth - minDepth);
    }
  }

  const depthGroups = new Map();
  for (const [nid] of nodesMap.entries()) {
    const d = depthMemo.get(nid) || 0;
    if (!depthGroups.has(d)) depthGroups.set(d, []);
    depthGroups.get(d).push(nid);
  }
  const sortedDepths = Array.from(depthGroups.keys()).sort((a, b) => a - b);

  // order children inside level (keeps your gender-based logic)
  const orderedLevels = [];
  for (const d of sortedDepths) {
    const levelNodeIds = depthGroups.get(d);
    const parentToChildren = new Map();
    for (const nid of levelNodeIds) {
      const parentSet = parentsMap.get(nid);
      if (!parentSet || parentSet.size === 0) continue;
      for (const pid of parentSet) {
        if (!parentToChildren.has(pid)) parentToChildren.set(pid, []);
        parentToChildren.get(pid).push(nid);
      }
    }
    const arranged = new Set();
    const finalLevelOrder = [];
    for (const pid of parentToChildren.keys()) {
      const pNode = nodesMap.get(pid);
      const kids = parentToChildren.get(pid) || [];
      if (pNode && pNode.type === "couple" && pNode.persons.length === 2) {
        const spouseA = pNode.persons[0];
        const spouseB = pNode.persons[1];
        const gA = normalizeGender(spouseA.gender);
        const gB = normalizeGender(spouseB.gender);
        let male = null;
        let female = null;
        if (gA === "male") male = spouseA;
        if (gA === "female") female = spouseA;
        if (!male && gB === "male") male = spouseB;
        if (!female && gB === "female") female = spouseB;
        if (!male && !female) {
          male = spouseA;
          female = spouseB;
        } else {
          if (!male) male = spouseA === female ? spouseB : spouseA;
          if (!female) female = spouseA === male ? spouseB : spouseA;
        }
        if (male && female) {
          const left = [];
          const neutral = [];
          const right = [];
          for (const kid of kids) {
            if (arranged.has(kid)) continue;
            const linkSet = parentChildMap ? parentChildMap.get(`${pid}::${kid}`) : undefined;
            const hasMaleLineage = Boolean(linkSet && linkSet.has(male.id));
            const hasFemaleLineage = Boolean(linkSet && linkSet.has(female.id));
            if (hasMaleLineage && !hasFemaleLineage) left.push(kid);
            else if (hasFemaleLineage && !hasMaleLineage) right.push(kid);
            else neutral.push(kid);
          }
          const ordered = [...left, ...neutral, ...right];
          for (const k of ordered) {
            if (!arranged.has(k)) {
              arranged.add(k);
              finalLevelOrder.push(k);
            }
          }
          continue;
        }
      }
      for (const k of kids) {
        if (!arranged.has(k)) {
          arranged.add(k);
          finalLevelOrder.push(k);
        }
      }
    }
    for (const nid of levelNodeIds) {
      if (!arranged.has(nid)) {
        arranged.add(nid);
        finalLevelOrder.push(nid);
      }
    }
    orderedLevels.push(finalLevelOrder);
  }

  // keep male parent's parents before female parent's parents (same as you had)
  for (const node of nodesMap.values()) {
    if (node.type !== "couple" || node.persons.length !== 2) continue;
    const malePerson = node.persons.find((p) => normalizeGender(p.gender) === "male") || node.persons[0];
    const femalePerson = node.persons.find((p) => normalizeGender(p.gender) === "female" && (!malePerson || p.id !== malePerson.id))
      || node.persons.find((p) => p.id !== malePerson.id) || null;
    if (!malePerson || !femalePerson) continue;
    const maleParentNodeId = malePerson.parentId ? personToNode.get(malePerson.parentId) : null;
    const femaleParentNodeId = femalePerson.parentId ? personToNode.get(femalePerson.parentId) : null;
    if (!maleParentNodeId || !femaleParentNodeId) continue;
    const depthMale = depthMemo.get(maleParentNodeId);
    const depthFemale = depthMemo.get(femaleParentNodeId);
    if (depthMale === undefined || depthFemale === undefined) continue;
    if (depthMale !== depthFemale) continue;
    const level = orderedLevels[depthMale];
    if (!level) continue;
    const maleIndex = level.indexOf(maleParentNodeId);
    const femaleIndex = level.indexOf(femaleParentNodeId);
    if (maleIndex === -1 || femaleIndex === -1) continue;
    if (maleIndex > femaleIndex) [level[maleIndex], level[femaleIndex]] = [level[femaleIndex], level[maleIndex]];
  }

  // assign x positions per level
  for (let li = 0; li < orderedLevels.length; li++) {
    const level = orderedLevels[li];
    const y = 60 + li * V_GAP;
    if (li === 0) {
      let curX = 0;
      for (const nid of level) {
        const node = nodesMap.get(nid);
        if (!node) continue;
        const w = nodeWidth(node);
        positions.set(nid, { x: curX + w / 2, y, width: w });
        curX += w + H_GAP;
      }
      continue;
    }
    const prevLevel = orderedLevels[li - 1];
    const groups = [];
    const accounted = new Set();
    for (const pid of prevLevel) {
      const gChildren = level.filter((cid) => {
        if (accounted.has(cid)) return false;
        const pset = parentsMap.get(cid);
        return pset ? pset.has(pid) : false;
      });
      if (!gChildren.length) continue;
      const childNode = nodesMap.get(pid);
      if (childNode && childNode.type === "couple" && childNode.persons.length >= 2 && gChildren.length > 1) {
        const spouseA = childNode.persons[0];
        const spouseB = childNode.persons[1];
        const gA = normalizeGender(spouseA.gender);
        const gB = normalizeGender(spouseB.gender);
        let malePerson = null;
        let femalePerson = null;
        if (gA === "male") malePerson = spouseA;
        if (gA === "female") femalePerson = spouseA;
        if (!malePerson && gB === "male") malePerson = spouseB;
        if (!femalePerson && gB === "female") femalePerson = spouseB;
        if (!malePerson && !femalePerson) {
          malePerson = spouseA;
          femalePerson = spouseB;
        } else {
          if (!malePerson) malePerson = spouseA === femalePerson ? spouseB : spouseA;
          if (!femalePerson) femalePerson = spouseA === malePerson ? spouseB : spouseA;
        }
        const maleParentNodeId = malePerson && malePerson.parentId ? personToNode.get(malePerson.parentId) : null;
        const femaleParentNodeId = femalePerson && femalePerson.parentId ? personToNode.get(femalePerson.parentId) : null;
        if (maleParentNodeId && femaleParentNodeId && maleParentNodeId !== femaleParentNodeId) {
          gChildren.sort((a, b) => {
            const rank = (nodeId) => {
              if (nodeId === maleParentNodeId) return 0;
              if (nodeId === femaleParentNodeId) return 1;
              return 2;
            };
            return rank(a) - rank(b);
          });
        }
      }
      groups.push({ parentId: pid, children: gChildren });
      gChildren.forEach((id) => accounted.add(id));
    }
    for (const cid of level) {
      if (!accounted.has(cid)) groups.push({ parentId: null, children: [cid] });
    }
    let curX = 0;
    for (const group of groups) {
      for (let i = 0; i < group.children.length; i++) {
        const nid = group.children[i];
        const node = nodesMap.get(nid);
        if (!node) continue;
        const w = nodeWidth(node);
        positions.set(nid, { x: curX + w / 2, y, width: w });
        curX += w + H_GAP;
      }
      curX += CHILD_GROUP_GAP;
      if (group.parentId) {
        const parentPos = positions.get(group.parentId);
        const childXs = group.children.map((cid) => positions.get(cid)?.x).filter(Boolean);
        if (parentPos && childXs.length) {
          const spanMin = Math.min(...childXs);
          const spanMax = Math.max(...childXs);
          const newParentX = (spanMin + spanMax) / 2;
          positions.set(group.parentId, { ...parentPos, x: newParentX });
        }
      }
    }
  }

  const levels = orderedLevels;

  const orientParents = () => {
    for (const node of nodesMap.values()) {
      if (node.type !== "couple" || node.persons.length !== 2) continue;
      const spouseA = node.persons[0];
      const spouseB = node.persons[1];
      const gA = normalizeGender(spouseA.gender);
      const gB = normalizeGender(spouseB.gender);
      let male = null;
      let female = null;
      if (gA === "male") male = spouseA;
      if (gA === "female") female = spouseA;
      if (!male && gB === "male") male = spouseB;
      if (!female && gB === "female") female = spouseB;
      if (!male && !female) {
        male = spouseA;
        female = spouseB;
      } else {
        if (!male) male = spouseA === female ? spouseB : spouseA;
        if (!female) female = spouseA === male ? spouseB : spouseA;
      }
      if (!male || !female) continue;
      const cpos = positions.get(node.id);
      if (!cpos) continue;

      const maleParentNodeId = male.parentId ? personToNode.get(male.parentId) : null;
      const femaleParentNodeId = female.parentId ? personToNode.get(female.parentId) : null;
      if (!maleParentNodeId && !femaleParentNodeId) continue;

      const mpPos = maleParentNodeId ? positions.get(maleParentNodeId) : null;
      const fpPos = femaleParentNodeId ? positions.get(femaleParentNodeId) : null;
      const parentLevelY = cpos.y - V_GAP;

      if (mpPos && fpPos && Math.abs(mpPos.y - parentLevelY) < 1e-3 && Math.abs(fpPos.y - parentLevelY) < 1e-3) {
        if (mpPos.x <= fpPos.x - H_GAP) continue;
        const mpNode = nodesMap.get(maleParentNodeId);
        const fpNode = nodesMap.get(femaleParentNodeId);
        const mpWidth = mpNode ? (mpNode.type === "single" ? NODE_W : NODE_W * 2 + COUPLE_GAP) : NODE_W;
        const fpWidth = fpNode ? (fpNode.type === "single" ? NODE_W : NODE_W * 2 + COUPLE_GAP) : NODE_W;
        const gap = H_GAP;
        const totalWidth = mpWidth + gap + fpWidth;
        const leftEdge = cpos.x - totalWidth / 2;
        const maleCenter = leftEdge + mpWidth / 2;
        const femaleCenter = maleCenter + mpWidth / 2 + gap + fpWidth / 2;
        positions.set(maleParentNodeId, { ...mpPos, x: maleCenter });
        positions.set(femaleParentNodeId, { ...fpPos, x: femaleCenter });
      } else {
        const leftSpouseCenter = cpos.x - (NODE_W / 2 + COUPLE_GAP / 2);
        const rightSpouseCenter = cpos.x + (NODE_W / 2 + COUPLE_GAP / 2);
        if (mpPos && Math.abs(mpPos.y - parentLevelY) < 1e-3 && Math.abs(mpPos.x - leftSpouseCenter) > 1) {
          positions.set(maleParentNodeId, { ...mpPos, x: leftSpouseCenter });
        }
        if (fpPos && Math.abs(fpPos.y - parentLevelY) < 1e-3 && Math.abs(fpPos.x - rightSpouseCenter) > 1) {
          positions.set(femaleParentNodeId, { ...fpPos, x: rightSpouseCenter });
        }
      }
    }
  };

  for (let li = levels.length - 2; li >= 0; li--) {
    const level = levels[li];
    for (const nid of level) {
      const node = nodesMap.get(nid);
      if (!node || !node.children.size) continue;
      const childXs = [];
      for (const cid of node.children) {
        const cpos = positions.get(cid);
        if (cpos) childXs.push(cpos.x);
      }
      if (!childXs.length) continue;
      childXs.sort((a,b)=>a-b);
      const newX = (childXs[0] + childXs[childXs.length - 1]) / 2;
      const pos = positions.get(nid);
      if (pos) positions.set(nid, { ...pos, x: newX });
    }
  }

  const collectSubtree = (rootId, acc = new Set()) => {
    if (acc.has(rootId)) return acc;
    acc.add(rootId);
    const n = nodesMap.get(rootId);
    if (n && n.children) {
      for (const cid of n.children) collectSubtree(cid, acc);
    }
    return acc;
  };

  orientParents();

  for (let li = 0; li < levels.length; li++) {
    const level = levels[li];
    const posArray = level.map((nid) => ({
      nid,
      pos: positions.get(nid),
      width: (positions.get(nid) ? positions.get(nid).width : nodeWidth(nodesMap.get(nid))) || NODE_W,
    })).filter(p => p.pos);
    posArray.sort((a,b) => (a.pos.x - a.width/2) - (b.pos.x - b.width/2));
    let cursorRight = -Infinity;
    for (const item of posArray) {
      const left = item.pos.x - item.width / 2;
      if (left < cursorRight) {
        const neededShift = cursorRight - left;
        const subtree = collectSubtree(item.nid);
        for (const sid of subtree) {
          const sp = positions.get(sid);
          if (sp) positions.set(sid, { ...sp, x: sp.x + neededShift });
        }
      }
      const updatedPos = positions.get(item.nid);
      cursorRight = Math.max(cursorRight, updatedPos.x + item.width/2 + H_GAP);
    }
  }

  // orientParents();

  // recenter
  const rootPositionsAfter = roots.map(r => positions.get(r)).filter(Boolean);
  if (rootPositionsAfter.length) {
    const minRootX = Math.min(...rootPositionsAfter.map(p=>p.x - p.width/2));
    const maxRootX = Math.max(...rootPositionsAfter.map(p=>p.x + p.width/2));
    const treeCenter = (minRootX + maxRootX) / 2;
    for (const [nid,pos] of positions.entries()) {
      positions.set(nid, { ...pos, x: pos.x - treeCenter });
    }
  }

  return { positions, levels };
}

// collapse/expand logic removed

/* ---------- Component ---------- */
export default function FamilyTreeUi({ members, loading = false, onOpenAddFamily, onLinkSuccess, familyGroupId = "" }) {
  const persons = Array.isArray(members) ? members : [];
  const personMap = useMemo(() => buildPersonMap(persons), [persons]);
  const resolveSpouse = useCallback((person) => resolveMutualSpouse(person, personMap), [personMap]);

  const { nodes: nodesMap, personToNode, parentsMap, parentChildMap } = useMemo(() => buildNodes(persons), [persons]);

  const { roots, nodes } = useMemo(() => buildForest(nodesMap), [nodesMap]);

  const layout = useMemo(
    () => computeLayout(nodes, roots, personMap, personToNode, parentsMap, parentChildMap),
    [nodes, roots, personMap, personToNode, parentsMap, parentChildMap]
  );
  const nodePositions = layout.positions || new Map();
  const orderedLevels = layout.levels || [];

  const initialCoords = useMemo(() => {
    const coords = new Map();
    for (const [nid, node] of nodes.entries()) {
      const pos = nodePositions.get(nid);
      if (!pos) continue;
      const cx = pos.x;
      const cy = pos.y;
      if (node.type === "single") {
        coords.set(node.persons[0].id, { x: cx, y: cy });
      } else {
        const offset = NODE_W / 2 + COUPLE_GAP / 2;
        coords.set(node.persons[0].id, { x: cx - offset, y: cy });
        coords.set(node.persons[1].id, { x: cx + offset, y: cy });
      }
    }
    return coords;
  }, [nodes, nodePositions]);

  // collapse/expand removed; no descendant overlap analysis

  const nodeSubtreePersonMap = useMemo(() => {
    const cache = new Map();
    const visit = (nodeId) => {
      if (cache.has(nodeId)) return cache.get(nodeId);
      const node = nodes.get(nodeId);
      const set = new Set();
      if (!node) {
        cache.set(nodeId, set);
        return set;
      }
      for (const person of node.persons) set.add(person.id);
      if (node.children) {
        for (const childId of node.children) {
          const childSet = visit(childId);
          childSet.forEach((id) => set.add(id));
        }
      }
      cache.set(nodeId, set);
      return set;
    };
    for (const nid of nodes.keys()) visit(nid);
    return cache;
  }, [nodes]);

  const [posMap, setPosMap] = useState({});

  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(0.3);
  const [isRelationshipMode, setIsRelationshipMode] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [highlightColor, setHighlightColor] = useState("#ff0000");
  const glowId = useMemo(() => `glowHL-${highlightColor.replace('#', '')}`, [highlightColor]);
  const [currentRelationshipPath, setCurrentRelationshipPath] = useState(null);
  const [selectedPersons, setSelectedPersons] = useState([]);
  const [pathPersons, setPathPersons] = useState(new Set());
  const [relPairEdges, setRelPairEdges] = useState(() => new Set());
  const [selectedPersonPopup, setSelectedPersonPopup] = useState(null);
  const selectedSpouse = selectedPersonPopup ? resolveSpouse(selectedPersonPopup) : null;
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [familyGroupCopied, setFamilyGroupCopied] = useState(false);

  // Sticky header + scroll behavior
  const sectionRef = useRef(null);
  const [stickyTop, setStickyTop] = useState(64);
  const [hideHeader, setHideHeader] = useState(false);
  const lastScrollYRef = useRef(0);

  const handleCopyFamilyGroupId = useCallback(async () => {
    if (!familyGroupId) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(familyGroupId);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = familyGroupId;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "absolute";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setFamilyGroupCopied(true);
      window.setTimeout(() => setFamilyGroupCopied(false), 1500);
    } catch (err) {
      console.error("Failed to copy Family Group ID:", err);
    }
  }, [familyGroupId]);

  const edgeKey = useCallback((a, b) => {
    if (a === undefined || b === undefined || a === null || b === null) return "";
    return String(a) < String(b) ? `${a}|${b}` : `${b}|${a}`;
  }, []);

  // Build an undirected adjacency graph (parent-child + spouse links)
  const adjacency = useMemo(() => {
    const adj = new Map();
    const add = (x, y) => {
      if (!x || !y) return;
      if (!adj.has(x)) adj.set(x, new Set());
      if (!adj.has(y)) adj.set(y, new Set());
      adj.get(x).add(y);
      adj.get(y).add(x);
    };
    for (const p of persons) {
      if (p.parentId) add(p.id, p.parentId);
    }
    for (const node of nodes.values()) {
      if (node.type === "couple" && node.persons.length === 2) {
        add(node.persons[0].id, node.persons[1].id);
      }
    }
    return adj;
  }, [persons, nodes]);

  const findPath = useCallback((startId, endId) => {
    if (!startId || !endId || startId === endId) return null;
    const q = [startId];
    const visited = new Set([startId]);
    const prev = new Map();
    while (q.length) {
      const u = q.shift();
      if (u === endId) break;
      const neigh = adjacency.get(u) || new Set();
      for (const v of neigh) {
        if (!visited.has(v)) {
          visited.add(v);
          prev.set(v, u);
          q.push(v);
        }
      }
    }
    if (!visited.has(endId)) return null;
    const path = [];
    let cur = endId;
    while (cur !== undefined) {
      path.push(cur);
      if (cur === startId) break;
      cur = prev.get(cur);
    }
    path.reverse();
    return path;
  }, [adjacency]);

  const handlePairSelected = useCallback((p1, p2) => {
    if (p1 && p2) {
      setSelectedPersons([p1.id, p2.id]);
      const path = findPath(p1.id, p2.id);
      setCurrentRelationshipPath(path);
      if (path && path.length > 1) {
        setPathPersons(new Set(path));
        const set = new Set();
        for (let i = 0; i < path.length - 1; i++) {
          set.add(edgeKey(path[i], path[i + 1]));
        }
        setRelPairEdges(set);
      } else {
        setPathPersons(new Set());
        setRelPairEdges(new Set());
      }
    } else {
      setSelectedPersons([]);
      setPathPersons(new Set());
      setRelPairEdges(new Set());
      setCurrentRelationshipPath(null);
    }
  }, [findPath, edgeKey]);

  // collapse/expand removed; no collapsed state

  const relationshipClickHandlerRef = useRef(null);
  const svgRef = useRef(null);
  const draggingRef = useRef(null);

  const getCoord = useCallback(
    (personId) => {
      if (posMap[personId]) return posMap[personId];
      const init = initialCoords.get(personId);
      if (init) return init;
      return { x: 0, y: 0 };
    },
    [posMap, initialCoords]
  );

  const marriageCenters = useMemo(() => {
    const m = new Map();
    for (const node of nodes.values()) {
      if (node.type !== "couple") continue;
      const a = node.persons[0];
      const b = node.persons[1];
      const ca = getCoord(a.id);
      const cb = getCoord(b.id);
      m.set(`${a.id}_${b.id}`, { x: (ca.x + cb.x) / 2, y: (ca.y + cb.y) / 2 });
      m.set(`${b.id}_${a.id}`, { x: (ca.x + cb.x) / 2, y: (ca.y + cb.y) / 2 });
    }
    return m;
  }, [nodes, getCoord]);

  // collapse/expand removed; hasParents no longer needed

  // No collapse: nothing is hidden
  const hiddenPersonIds = useMemo(() => new Set(), []);

  const visiblePersonIds = useMemo(() => {
    const visible = new Set(persons.map((p) => p.id));
    for (const hiddenId of hiddenPersonIds) visible.delete(hiddenId);
    return visible;
  }, [persons, hiddenPersonIds]);

  // connections (parent->child) respect visibility: if child's parents are hidden (child not in visible set), skip
  const connections = useMemo(() => {
    const rawConns = [];
    const branchBaselines = new Map(); // remembers the "sibling line" for each parent group
    for (const p of persons) {
      if (!p.parentId) continue;
      // if this child's parents are hidden globally -> skip
      if (!visiblePersonIds.has(p.id)) continue;

      const childCoord = getCoord(p.id);
      const parentPerson = personMap.get(p.parentId);
      if (!parentPerson) continue;
      // if parent is not visible, still draw connection? we skip if parent not visible (keeps tree clean)
      const parentNodeId = personToNode.get(parentPerson.id);
      const parentNode = nodes.get(parentNodeId);
      let origin = null;
      let parentIds = [];
      if (parentNode && parentNode.type === "couple") {
        const spouseA = parentNode.persons[0];
        const spouseB = parentNode.persons[1];
        const cA = getCoord(spouseA.id);
        const cB = getCoord(spouseB.id);
        parentIds = [spouseA.id, spouseB.id];
        if (cA && cB && visiblePersonIds.has(spouseA.id) && visiblePersonIds.has(spouseB.id)) {
          origin = {
            x: (cA.x + cB.x) / 2,
            y: Math.max(cA.y, cB.y) + NODE_H / 2,
          };
        } else {
          // If only one spouse visible, use that spouse as origin
          if (visiblePersonIds.has(spouseA.id)) origin = { x: cA.x, y: cA.y + NODE_H / 2 };
          else if (visiblePersonIds.has(spouseB.id)) origin = { x: cB.x, y: cB.y + NODE_H / 2 };
          else origin = null;
        }
      } else {
        const pc = getCoord(parentPerson.id);
        if (pc && visiblePersonIds.has(parentPerson.id)) origin = { x: pc.x, y: pc.y + NODE_H / 2 };
        parentIds = [parentPerson.id];
      }
      if (!origin) continue;
      // Adjust endpoint above node top and introduce a horizontal jog if vertical alignment causes overlap.
      const CONNECT_OFFSET = 8; // distance above node top for clearer halo
      const VERTICAL_JOG = 10; // horizontal jog distance for perfectly vertical links
      const targetTop = childCoord.y - NODE_H / 2;
      const targetY = targetTop - CONNECT_OFFSET;
      const target = { x: childCoord.x, y: targetY };
      const midY = (origin.y + target.y) / 2;
      const isPerfectVertical = Math.abs(origin.x - target.x) < 0.5;
      const longDrop = target.y - origin.y > V_GAP + 40;
      // Choose jog direction: try to bias to the right unless parent is already right of child
      const jogDir = origin.x > target.x ? -1 : 1;
      const parentKey = parentIds.length
        ? parentIds
            .map((id) => id?.toString() ?? "")
            .sort()
            .join("_")
        : `parent_${p.parentId}`;
      rawConns.push({
        key: `${p.id}_${p.parentId}`,
        origin,
        target,
        midY,
        isPerfectVertical,
        longDrop,
        jogDir,
        parentIds,
        parentKey,
        childId: p.id,
      });
      if (!longDrop && parentKey) {
        const existing = branchBaselines.get(parentKey);
        branchBaselines.set(parentKey, existing === undefined ? midY : Math.min(existing, midY));
      }
    }
    const conns = [];
    for (const entry of rawConns) {
      const { origin, target, midY, isPerfectVertical, longDrop, jogDir, parentIds, parentKey, key, childId } = entry;
      const CONNECT_OFFSET = 8;
      const VERTICAL_JOG = 10;
      let path;
      if (longDrop) {
        let baseline = branchBaselines.get(parentKey);
        if (baseline === undefined) baseline = origin.y + V_GAP / 2;
        if (baseline <= origin.y) baseline = origin.y + CONNECT_OFFSET;
        if (baseline >= target.y) baseline = Math.max(origin.y + CONNECT_OFFSET, target.y - CONNECT_OFFSET);
        // Share the same branch band as siblings, then drop vertically to the lowered node
        path = `M ${origin.x} ${origin.y} L ${origin.x} ${baseline} L ${target.x} ${baseline} L ${target.x} ${target.y}`;
      } else if (isPerfectVertical) {
        const jogX = target.x + jogDir * VERTICAL_JOG;
        path = `M ${origin.x} ${origin.y} L ${origin.x} ${midY} L ${jogX} ${midY} L ${jogX} ${target.y} L ${target.x} ${target.y}`;
      } else {
        path = `M ${origin.x} ${origin.y} L ${origin.x} ${midY} L ${target.x} ${midY} L ${target.x} ${target.y}`;
      }
      conns.push({ key, path, childId, parentIds });
    }
    return conns;
  }, [persons, personMap, personToNode, nodes, marriageCenters, getCoord, visiblePersonIds]);

  // which parent nodes have visible children (used for shouldRenderNode)
  const parentNodesWithVisibleChildren = useMemo(() => {
    const set = new Set();
    for (const ch of persons) {
      if (!ch.parentId) continue;
      if (!visiblePersonIds.has(ch.id)) continue;
      const parentNodeId = personToNode.get(ch.parentId);
      if (parentNodeId) set.add(parentNodeId);
    }
    return set;
  }, [persons, personToNode, visiblePersonIds]);

  const shouldRenderNode = useCallback((node) => {
    if (!node) return false;
    // hide node if none of its persons are visible
    const anyPersonVisible = node.persons.some((p) => visiblePersonIds.has(p.id));
    if (!anyPersonVisible) return false;
    // render if node has visible children
    if (parentNodesWithVisibleChildren.has(node.id)) return true;
    // If leaf, show if its person visible
    if (!node.children || node.children.size === 0) return true;
    // otherwise show if any node person remains visible (avoids hiding parents unnecessarily)
    return anyPersonVisible;
  }, [parentNodesWithVisibleChildren, visiblePersonIds]);

  // relationship helpers (unchanged)
  const findParents = useCallback((person) => {
    if (!person || !person.parentId) return [];
    const parent1 = personMap.get(person.parentId);
    if (!parent1) return [];
    const parent2 = parent1.spouseId ? personMap.get(parent1.spouseId) || null : null;
    return parent2 ? [parent1, parent2] : [parent1];
  }, [personMap]);

  const calculateRelationship = useCallback((person1, person2) => {
    // keep your existing relationship calculation (kept minimal here)
    if (!person1 || !person2) return { forward: "", reverse: "" };
    if (person1.id === person2.id) return { forward: "Same person", reverse: "" };
    // spouse
    if (person1.spouseId === person2.id || person2.spouseId === person1.id) {
      const term1 = person1.gender === "male" ? "Husband" : "Wife";
      const term2 = person2.gender === "male" ? "Husband" : "Wife";
      return { forward: `${person1.name} is ${term1} of ${person2.name}`, reverse: `${person2.name} is ${term2} of ${person1.name}` };
    }
    return { forward: "Relationship not computed (placeholder)", reverse: "" };
  }, []);

  const getNodeStatus = useCallback((node) => {
    if (!isRelationshipMode || selectedPersons.length === 0) return 'normal';
    const persons = node.persons.map(p => p.id);
    const hasSelected = persons.some(id => selectedPersons.includes(id));
    if (hasSelected) return 'selected';
    const hasPath = persons.some(id => pathPersons.has(id));
    if (hasPath) return 'intermediate';
    return 'other';
  }, [isRelationshipMode, selectedPersons, pathPersons]);

  const handleNodeClick = useCallback((personId) => {
    if (!isRelationshipMode) return;
    const person = personMap.get(personId);
    if (!person) return;
    if (relationshipClickHandlerRef.current) relationshipClickHandlerRef.current(person);
  }, [isRelationshipMode, personMap]);

  const handleToggleRelationship = useCallback(() => {
    const next = !drawerOpen;
    setDrawerOpen(next);
    setIsRelationshipMode(next);
    if (!next) {
      setSelectedPersons([]);
      setPathPersons(new Set());
      setRelPairEdges(new Set());
    }
  }, [drawerOpen]);

  /* ---------- interactions: drag node/pan/zoom ---------- */
  const clientToWorld = useCallback((clientX, clientY) => {
    const svg = svgRef.current;
    if (!svg) return { x: clientX, y: clientY };
    const rect = svg.getBoundingClientRect();
    const px = clientX - rect.left;
    const py = clientY - rect.top;
    return { x: (px - pan.x) / scale, y: (py - pan.y) / scale };
  }, [pan, scale]);

  const handleNodePointerDown = useCallback((e, personId) => {
    e.stopPropagation();
    const start = { x: e.clientX, y: e.clientY };
    const nodeId = personToNode.get(personId);
    if (!nodeId) return;
    const node = nodes.get(nodeId);
    if (!node) return;

    const onMove = (ev) => {
      const d = draggingRef.current;
      if (!d || d.type !== "node") return;
      const dx = (ev.clientX - d.startPointer.x) / scale;
      const dy = (ev.clientY - d.startPointer.y) / scale;
      if (Math.abs(dx) > 2 || Math.abs(dy) > 2) d.moved = true;
      const newPositions = {};
      for (const pid of Object.keys(d.startPositions)) {
        const sp = d.startPositions[pid];
        newPositions[pid] = { x: sp.x + dx, y: sp.y + dy };
      }
      setPosMap((prev) => ({ ...prev, ...newPositions }));
    };

    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      const drag = draggingRef.current;
      draggingRef.current = null;
      if (!drag) return;
      if (drag.type === "node" && !drag.moved) {
        if (isRelationshipMode) handleNodeClick(drag.personId);
        else {
          setSelectedNodeId(drag.personId);
          setSelectedPersonPopup(personMap.get(drag.personId));
        }
      }
    };

    draggingRef.current = {
      type: "node",
      nodeId,
      personId,
      moved: false,
      startPointer: start,
      startPositions: (() => {
        const res = {};
        for (const person of node.persons) res[person.id] = getCoord(person.id);
        return res;
      })(),
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }, [getCoord, nodes, personToNode, scale, isRelationshipMode, handleNodeClick]);

  const handleBgPointerDown = useCallback((e) => {
    if (e.target.closest && e.target.closest(".node-card")) return;

    const onMove = (ev) => {
      const d = draggingRef.current;
      if (!d || d.type !== "pan") return;
      const dx = ev.clientX - d.startPointer.x;
      const dy = ev.clientY - d.startPointer.y;
      setPan({ x: d.startPan.x + dx, y: d.startPan.y + dy });
      if (Math.abs(dx) > 2 || Math.abs(dy) > 2) d.moved = true;
    };

    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      draggingRef.current = null;
    };

    draggingRef.current = {
      type: "pan",
      startPointer: { x: e.clientX, y: e.clientY },
      startPan: { ...pan },
      moved: false,
    };

    setSelectedNodeId(null);
    setSelectedPersonPopup(null);

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }, [pan]);

  const onWheel = useCallback((e) => {
    e.preventDefault();
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const before = clientToWorld(e.clientX, e.clientY);
    const delta = -e.deltaY;
    const factor = delta > 0 ? 1.12 : 0.88;
    const nextScale = Math.max(0.25, Math.min(3, scale * factor));
    setScale(nextScale);
    const afterPanX = e.clientX - rect.left - before.x * nextScale;
    const afterPanY = e.clientY - rect.top - before.y * nextScale;
    setPan({ x: afterPanX, y: afterPanY });
  }, [clientToWorld, scale]);

  // Attach non-passive wheel listener so preventDefault works without warning
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    svg.addEventListener('wheel', onWheel, { passive: false });
    return () => svg.removeEventListener('wheel', onWheel);
  }, [onWheel]);

  // Measure navbar height to offset sticky header correctly
  useEffect(() => {
    const measure = () => {
      let h = 64;
      try {
        const el = document.querySelector('header .MuiAppBar-root')
          || document.querySelector('.MuiAppBar-root')
          || document.querySelector('header[role="banner"]')
          || document.querySelector('header');
        if (el) h = Math.max(0, el.getBoundingClientRect().height);
      } catch (_) {}
      setStickyTop(h || 64);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  // Hide header when scrolling down past the tree; show on scroll up
  useEffect(() => {
    const onScroll = () => {
      const currentY = window.scrollY || document.documentElement.scrollTop || 0;
      const lastY = lastScrollYRef.current || 0;
      const scrollingDown = currentY > lastY;
      lastScrollYRef.current = currentY;

      const section = sectionRef.current;
      if (!section) return;
      const rect = section.getBoundingClientRect();
      const pastTree = rect.bottom <= (stickyTop + 4);

      if (!scrollingDown) {
        // Always show on upward scroll
        setHideHeader(false);
      } else if (scrollingDown && pastTree) {
        // Hide when the user scrolls down beyond the tree (members list area)
        setHideHeader(true);
      } else {
        // Inside tree area -> keep visible
        setHideHeader(false);
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [stickyTop]);

  // ensure posMap contains initial coords
  useEffect(() => {
    setPosMap((prev) => {
      const obj = { ...prev };
      for (const [k, v] of initialCoords.entries()) {
        if (!obj[k]) obj[k] = { ...v };
      }
      return obj;
    });
  }, [initialCoords]);

  // When the underlying members/nodes change (e.g., after adding a member
  // and refetching), clear any user-drag overrides so the fresh layout
  // doesn't overlap with stale positions.
  useEffect(() => {
    // Reset positions to computed initial coordinates
    const next = {};
    for (const [k, v] of initialCoords.entries()) {
      next[k] = { ...v };
    }
    setPosMap(next);
  }, [persons, nodes]);

  // center on load
  useEffect(() => {
    const svg = svgRef.current;
    if (svg && initialCoords.size > 0) {
      const rect = svg.getBoundingClientRect();
      const allCoords = Array.from(initialCoords.values());
      const minX = Math.min(...allCoords.map((c) => c.x)) - NODE_W * 1.5;
      const maxX = Math.max(...allCoords.map((c) => c.x)) + NODE_W * 1.5;
      const minY = Math.min(...allCoords.map((c) => c.y)) - NODE_H * 1.5;
      const maxY = Math.max(...allCoords.map((c) => c.y)) + NODE_H * 1.5;
      const treeWidth = maxX - minX;
      const treeHeight = maxY - minY;
      const s = scale || 0.3;
      const centerX = -minX * s + (rect.width - treeWidth * s) / 2;
      const centerY = -minY * s + (rect.height - treeHeight * s) / 2;
      setPan({ x: centerX, y: centerY });
    }
  }, [initialCoords, scale]);

  // collapse/expand interaction removed

  /* ---------- render ---------- */
  const allCoords = Array.from(initialCoords.keys()).map((id) => {
    const p = posMap[id] || initialCoords.get(id) || { x: 0, y: 0 };
    return p;
  });
  const minX = Math.min(...allCoords.map((c) => c.x)) - NODE_W * 1.5;
  const maxX = Math.max(...allCoords.map((c) => c.x)) + NODE_W * 1.5;
  const minY = Math.min(...allCoords.map((c) => c.y)) - NODE_H * 1.5;
  const maxY = Math.max(...allCoords.map((c) => c.y)) + NODE_H * 1.5;
  const zoomPercent = Math.round(scale * 100);
  const nodeCursor = isRelationshipMode ? "pointer" : "grab";

  return (
    <>
      <div ref={sectionRef}>
      <Card sx={{ 
        position: "sticky", 
        top: stickyTop, 
        zIndex: 9, 
        transition: "transform 180ms ease, opacity 180ms ease", 
        transform: hideHeader ? "translateY(-110%)" : "translateY(0)", 
        opacity: hideHeader ? 0 : 1,
        boxShadow: { xs: 2, sm: 3 }
      }}>
        <MDBox p={{ xs: 1.5, sm: 2, md: 2.5 }}>
          <MDBox 
            display="flex" 
            flexDirection={{ xs: "column", sm: "row" }}
            justifyContent="space-between" 
            alignItems={{ xs: "stretch", sm: "center" }}
            gap={{ xs: 1.5, sm: 2 }}
            mb={{ xs: 1.5, sm: 2 }}
          >
            <MDBox display="flex" alignItems="center" gap={{ xs: 1, sm: 1.5 }} flexWrap="wrap">
              <MDTypography 
                variant="h5" 
                fontWeight="medium" 
                color="dark" 
                sx={{ 
                  lineHeight: 1.2,
                  fontSize: { xs: "1.125rem", sm: "1.25rem", md: "1.5rem" }
                }}
              >
                Family Tree
              </MDTypography>
              {familyGroupId && (
                <Tooltip title={familyGroupCopied ? "Copied" : "Click to copy"}>
                  <MDTypography
                    variant="caption"
                    color="text"
                    sx={{ ml: 1, fontWeight: 600, cursor: "pointer", textDecoration: "underline" }}
                    role="button"
                    tabIndex={0}
                    onClick={handleCopyFamilyGroupId}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleCopyFamilyGroupId();
                      }
                    }}
                  >
                    {`Family Group ID: ${familyGroupId}`}
                  </MDTypography>
                </Tooltip>
              )}
              {/* Highlight color options */}
              <Box sx={{ display: { xs: "none", lg: "flex" }, alignItems: "center", gap: 1 }}>
                <MDTypography variant="caption" color="text" sx={{ fontWeight: 600, fontSize: "0.75rem" }}>
                  Highlight:
                </MDTypography>
                {[
                  { name: "Red", val: "#ff1744" },
                  { name: "Orange", val: "#ff7a00" },
                  { name: "Amber", val: "#ffbf00" },
                  { name: "Lime", val: "#39ff14" },
                  { name: "Cyan", val: "#00e5ff" },
                  { name: "Electric Blue", val: "#00b0ff" },
                  { name: "Purple", val: "#b517ff" },
                  { name: "Hot Pink", val: "#ff2d55" },
                  { name: "Teal", val: "#00b894" },
                  { name: "Coral", val: "#ff6f61" },
                ].map((c) => (
                  <Tooltip key={c.val} title={`Highlight: ${c.name}`}>
                    <Box
                      onClick={() => setHighlightColor(c.val)}
                      sx={{
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        border: c.val.toLowerCase() === highlightColor.toLowerCase() ? "2px solid #111827" : "1px solid #e5e7eb",
                        boxShadow: c.val.toLowerCase() === highlightColor.toLowerCase() ? "0 0 0 2px #fff inset" : undefined,
                        cursor: "pointer",
                        background: c.val,
                        transition: "all 0.2s ease",
                        '&:hover': {
                          transform: "scale(1.15)",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
                        }
                      }}
                    />
                  </Tooltip>
                ))}
              </Box>
            </MDBox>
            <MDBox 
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              gap={{ xs: 0.75, sm: 0.75 }}
              alignItems={{ xs: "stretch", sm: "center" }}
              justifyContent={{ xs: "flex-start", sm: "flex-end" }}
            >
              {/* Row 1 on mobile: Add Member + Check Relationship */}
              <MDBox display="flex" gap={{ xs: 0.5, sm: 0.75 }} flexWrap="wrap">
                {typeof onOpenAddFamily === 'function' && (
                  <Tooltip title="Add Family Member">
                    <MDButton 
                      variant="gradient" 
                      color="primary" 
                      size="small" 
                      onClick={onOpenAddFamily} 
                      sx={{ 
                        py: { xs: 0.5, sm: 0.75 }, 
                        px: { xs: 0.9, sm: 1.5 },
                        fontSize: { xs: "0.75rem", sm: "0.8rem" },
                        minWidth: { xs: "auto", sm: "auto" }
                      }}
                    >
                      <Icon sx={{ mr: { xs: 0.25, sm: 0.5 }, fontSize: { xs: "1rem", sm: "1.125rem" } }}>add</Icon>
                      <Box component="span">Add Member</Box>
                    </MDButton>
                  </Tooltip>
                )}
                <Tooltip title="Link Existing Members">
                  <MDButton
                    variant="outlined"
                    color="info"
                    size="small"
                    onClick={() => setLinkDialogOpen(true)}
                    sx={{
                      py: { xs: 0.5, sm: 0.75 },
                      px: { xs: 0.9, sm: 1.5 },
                      fontSize: { xs: "0.75rem", sm: "0.8rem" },
                      minWidth: { xs: "auto", sm: "auto" }
                    }}
                  >
                    <Icon sx={{ mr: { xs: 0.25, sm: 0.5 }, fontSize: { xs: "1rem", sm: "1.125rem" } }}>link</Icon>
                    <Box component="span">Link Existing Members</Box>
                  </MDButton>
                </Tooltip>
                <Tooltip title="Check Relationship">
                  <MDButton
                    variant={drawerOpen ? "contained" : "outlined"}
                    color="success"
                    size="small"
                    onClick={() => { const next = !drawerOpen; setDrawerOpen(next); setIsRelationshipMode(next); }}
                    sx={{ 
                      alignItems: "center",
                      gap: { xs: 0.25, sm: 0.5 },
                      py: { xs: 0.35, sm: 0.75 },
                      px: { xs: 0.9, sm: 1.5 },
                      fontSize: { xs: "0.65rem", sm: "0.8rem" },
                      minWidth: { xs: 28, sm: "auto" }
                    }}
                  >
                    <Icon sx={{ fontSize: { xs: "0.9rem", sm: "1.125rem" } }}>people_alt</Icon>
                    <Box component="span">Check Relationship</Box>
                  </MDButton>
                </Tooltip>
              </MDBox>

              {/* Row 2 on mobile: Zoom controls */}
              <MDBox display="flex" gap={{ xs: 0.5, sm: 0.75 }} flexWrap="wrap">
                <Tooltip title="Zoom In">
                  <MDButton 
                    variant="outlined" 
                    color="info" 
                    iconOnly 
                    size="small" 
                    onClick={() => setScale((s) => Math.min(3, s * 1.12))} 
                    sx={{ 
                      minWidth: { xs: 28, sm: 32 }, 
                      width: { xs: 28, sm: 32 }, 
                      height: { xs: 28, sm: 32 },
                      p: 0
                    }}
                  >
                    <Icon sx={{ fontSize: { xs: "1rem", sm: "1.125rem" } }}>add</Icon>
                  </MDButton>
                </Tooltip>
                <Tooltip title="Zoom Out">
                  <MDButton 
                    variant="outlined" 
                    color="info" 
                    iconOnly 
                    size="small" 
                    onClick={() => setScale((s) => Math.max(0.25, s * 0.9))} 
                    sx={{ 
                      minWidth: { xs: 28, sm: 32 }, 
                      width: { xs: 28, sm: 32 }, 
                      height: { xs: 28, sm: 32 },
                      p: 0
                    }}
                  >
                    <Icon sx={{ fontSize: { xs: "1rem", sm: "1.125rem" } }}>remove</Icon>
                  </MDButton>
                </Tooltip>
                <Tooltip title="Reset View">
                  <MDButton 
                    variant="outlined" 
                    color="dark" 
                    iconOnly 
                    size="small" 
                    onClick={() => {
                      const svg = svgRef.current;
                      if (svg) {
                        const rect = svg.getBoundingClientRect();
                        const treeWidth = maxX - minX;
                        const treeHeight = maxY - minY;
                        const s = 0.3;
                        const centerX = -minX * s + (rect.width - treeWidth * s) / 2;
                        const centerY = -minY * s + (rect.height - treeHeight * s) / 2;
                        setPan({ x: centerX, y: centerY });
                      }
                      setScale(0.3);
                    }} 
                    sx={{ 
                      minWidth: { xs: 28, sm: 32 }, 
                      width: { xs: 28, sm: 32 }, 
                      height: { xs: 28, sm: 32 },
                      p: 0
                    }}
                  >
                    <Icon sx={{ fontSize: { xs: "1rem", sm: "1.125rem" } }}>refresh</Icon>
                  </MDButton>
                </Tooltip>
              </MDBox>
            </MDBox>
          </MDBox>

          <MDBox 
            display="flex" 
            gap={{ xs: 0.75, sm: 1, md: 1.5 }} 
            flexWrap="wrap"
            alignItems="center"
          >
            <Tooltip title="Male">
            <Box sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 0.5, sm: 0.75 },
              px: { xs: 1, sm: 1.5 },
              py: { xs: 0.4, sm: 0.5 },
              borderRadius: 2,
              background: "linear-gradient(195deg, #3b82f6, #1e40af)",
              color: "#fff",
              fontSize: { xs: "0.7rem", sm: "0.75rem" },
              fontWeight: 500,
              boxShadow: "0 2px 4px rgba(59, 130, 246, 0.3)",
              transition: "transform 0.2s ease",
              '&:hover': { transform: "translateY(-1px)" }
            }}>
              <Icon sx={{ fontSize: { xs: "0.85rem", sm: "0.9rem" } }}>male</Icon> 
              <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>Male</Box>
            </Box>
            </Tooltip>
            <Tooltip title="Female">
            <Box sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 0.5, sm: 0.75 },
              px: { xs: 1, sm: 1.5 },
              py: { xs: 0.4, sm: 0.5 },
              borderRadius: 2,
              background: "linear-gradient(195deg, #ec4899, #be185d)",
              color: "#fff",
              fontSize: { xs: "0.7rem", sm: "0.75rem" },
              fontWeight: 500,
              boxShadow: "0 2px 4px rgba(236, 72, 153, 0.3)",
              transition: "transform 0.2s ease",
              '&:hover': { transform: "translateY(-1px)" }
            }}>
              <Icon sx={{ fontSize: { xs: "0.85rem", sm: "0.9rem" } }}>female</Icon> 
              <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>Female</Box>
            </Box>
            </Tooltip>
            <Tooltip title="Couple">
            <Box sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 0.5, sm: 0.75 },
              px: { xs: 1, sm: 1.5 },
              py: { xs: 0.4, sm: 0.5 },
              borderRadius: 2,
              background: "linear-gradient(195deg, #ef4444, #dc2626)",
              color: "#fff",
              fontSize: { xs: "0.7rem", sm: "0.75rem" },
              fontWeight: 500,
              boxShadow: "0 2px 4px rgba(239, 68, 68, 0.3)",
              transition: "transform 0.2s ease",
              '&:hover': { transform: "translateY(-1px)" }
            }}>
              <Icon sx={{ fontSize: { xs: "0.85rem", sm: "0.9rem" } }}>favorite</Icon> 
              <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>Couple</Box>
            </Box>
            </Tooltip>
            <Box sx={{
              display: "flex",
              alignItems: "center",
              px: { xs: 1, sm: 1.5 },
              py: { xs: 0.4, sm: 0.5 },
              borderRadius: 2,
              border: "1.5px solid #3b82f6",
              background: "rgba(59, 130, 246, 0.05)",
              color: "#3b82f6",
              fontSize: { xs: "0.7rem", sm: "0.75rem" },
              fontWeight: 600,
              transition: "all 0.2s ease",
              '&:hover': { 
                background: "rgba(59, 130, 246, 0.1)",
                transform: "translateY(-1px)"
              }
            }}>
              <Box component="span" sx={{ display: { xs: "none", sm: "inline" }, mr: 0.5 }}>Zoom:</Box>
              {zoomPercent}%
            </Box>
          </MDBox>
        </MDBox>
      </Card>

      <Card sx={{ mt: 3, position: "relative" }}>
        <MDBox sx={{
          overflow: { xs: "auto", md: "visible" },
          position: "relative",
          background: "linear-gradient(to bottom, #f8fafc, #e2e8f0)",
          borderRadius: "0.75rem",
          minHeight: { xs: "70vh", md: "90vh" },
          maxHeight: { xs: "80vh", md: "none" },
          width: "100%",
          WebkitOverflowScrolling: "touch",
        }}>
          {loading && (
            <Box sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(255,255,255,0.6)',
              backdropFilter: 'blur(2px)'
            }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <CircularProgress color="primary" />
                <MDTypography variant="button" color="text">Loading tree…</MDTypography>
              </Box>
            </Box>
          )}
          <svg
            ref={svgRef}
            width="100%"
            height="100%"
            onPointerDown={handleBgPointerDown}
            style={{ touchAction: "none", userSelect: "none", display: "block", minHeight: window.innerWidth < 768 ? "70vh" : "90vh" }}
          >
            <defs>
              <linearGradient id={SELF_OVERLAY_GRADIENT_ID} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#fee2e2" stopOpacity="0.55" />
                <stop offset="55%" stopColor="#fca5a5" stopOpacity="0.22" />
                <stop offset="100%" stopColor="#fca5a5" stopOpacity="0" />
              </linearGradient>
              <linearGradient
                id={SELF_FLARE_GRADIENT_ID}
                x1="0%"
                y1="100%"
                x2="100%"
                y2="0%"
                gradientUnits="objectBoundingBox"
              >
                <stop offset="0%" stopColor="#ffffff" stopOpacity="0" />
                <stop stopColor="#ffffff" stopOpacity="0">
                  <animate attributeName="offset" values="-0.2;1.2" dur={`${SELF_FLARE_ANIMATION_DURATION}s`} repeatCount="indefinite" />
                </stop>
                <stop stopColor="#ffffff" stopOpacity="0.9">
                  <animate attributeName="offset" values="-0.15;1.25" dur={`${SELF_FLARE_ANIMATION_DURATION}s`} repeatCount="indefinite" />
                </stop>
                <stop stopColor="#ffffff" stopOpacity="0">
                  <animate attributeName="offset" values="-0.1;1.3" dur={`${SELF_FLARE_ANIMATION_DURATION}s`} repeatCount="indefinite" />
                </stop>
                <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
              </linearGradient>
              <filter id="soft" x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="0" dy="6" stdDeviation="8" floodColor="#000" floodOpacity="0.12" />
              </filter>
              <filter id={glowId} x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="0" dy="0" stdDeviation="4" floodColor={highlightColor} floodOpacity="0.9" />
                <feDropShadow dx="0" dy="0" stdDeviation="8" floodColor={highlightColor} floodOpacity="0.5" />
              </filter>
              <filter id={SELF_GLOW_FILTER_ID} x="-60%" y="-60%" width="240%" height="240%">
                <feGaussianBlur in="SourceGraphic" stdDeviation="5" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
              <filter id={SELF_GLOW_BLUR_ID} x="-80%" y="-80%" width="260%" height="260%">
                <feGaussianBlur stdDeviation="5" />
              </filter>
              <filter id="blur" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="2" />
              </filter>
            </defs>

            {/* world transform (pan + zoom) */}
            <g transform={`translate(${pan.x}, ${pan.y}) scale(${scale})`}>

              {/* connections */}
              <g stroke="#9aa3ad" strokeWidth={2} fill="none">
                {connections.map((c) => {
                  const edgeFromPair = isRelationshipMode && relPairEdges.size > 0 && Array.isArray(c.parentIds)
                    ? c.parentIds.some((pid) => relPairEdges.has(edgeKey(c.childId, pid)))
                    : false;
                  const isRelated = !isRelationshipMode && selectedNodeId && (c.childId === selectedNodeId || (Array.isArray(c.parentIds) && c.parentIds.includes(selectedNodeId)));
                  if (!(edgeFromPair || isRelated)) {
                    return (
                      <path
                        key={c.key}
                        d={c.path}
                        stroke="#9aa3ad"
                        strokeWidth={1.5}
                        fill="none"
                        style={{ opacity: 0.25 }}
                      />
                    );
                  }
                  return (
                    <g key={c.key} filter={`url(#${glowId})`}>
                      {/* halo for contrast */}
                      <path
                        d={c.path}
                        stroke={HIGHLIGHT_HALO_COLOR}
                        strokeWidth={HIGHLIGHT_HALO_WIDTH}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ opacity: 0.95 }}
                      />
                      {/* core colored stroke */}
                      <path
                        d={c.path}
                        stroke={highlightColor}
                        strokeWidth={HIGHLIGHT_CORE_WIDTH}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </g>
                  );
                })}
              </g>

              {/* marriage dots & lines */}
              <g>
                {Array.from(nodes.values()).filter((n) => n.type === "couple").map((node) => {
                  if (!shouldRenderNode(node)) return null;
                  const a = node.persons[0];
                  const b = node.persons[1];
                  if (!visiblePersonIds.has(a.id) && !visiblePersonIds.has(b.id)) return null;
                  const ca = getCoord(a.id);
                  const cb = getCoord(b.id);
                  const centerX = (ca.x + cb.x) / 2;
                  const centerY = (ca.y + cb.y) / 2;
                  const lineLeftX = ca.x + NODE_W / 2;
                  const lineRightX = cb.x - NODE_W / 2;
                  const pathHasMarriage = isRelationshipMode && relPairEdges.size > 0 && relPairEdges.has(edgeKey(a.id, b.id));
                  const isRelated = !isRelationshipMode && selectedNodeId && (a.id === selectedNodeId || b.id === selectedNodeId);
                  if (!(pathHasMarriage || isRelated)) {
                    return (
                      <g key={`mar-${node.id}`}>
                        <line x1={lineLeftX} y1={centerY} x2={centerX - MARRIAGE_DOT_R - 8} y2={centerY} stroke="#ff1744" strokeWidth={2.5} strokeLinecap="round" style={{ opacity: 0.9 }} />
                        <text
                          x={centerX}
                          y={centerY + 6}
                          textAnchor="middle"
                          fontSize={22}
                          fontWeight="bold"
                          fill="#ff1744"
                          style={{ opacity: 0.95 }}
                        >
                          &#9829;
                        </text>
                        <line x1={centerX + MARRIAGE_DOT_R + 8} y1={centerY} x2={lineRightX} y2={centerY} stroke="#ff1744" strokeWidth={2.5} strokeLinecap="round" style={{ opacity: 0.9 }} />
                      </g>
                    );
                  }
                  return (
                    <g key={`mar-${node.id}`} filter={`url(#${glowId})`}>
                      {/* halo */}
                      <line x1={lineLeftX} y1={centerY} x2={centerX - MARRIAGE_DOT_R - 8} y2={centerY} stroke={HIGHLIGHT_HALO_COLOR} strokeWidth={HIGHLIGHT_HALO_WIDTH} strokeLinecap="round" />
                      <line x1={centerX + MARRIAGE_DOT_R + 8} y1={centerY} x2={lineRightX} y2={centerY} stroke={HIGHLIGHT_HALO_COLOR} strokeWidth={HIGHLIGHT_HALO_WIDTH} strokeLinecap="round" />
                      <text
                        x={centerX}
                        y={centerY + 7}
                        textAnchor="middle"
                        fontSize={24}
                        fontWeight="bold"
                        fill={HIGHLIGHT_HALO_COLOR}
                      >
                        &#9829;
                      </text>

                      {/* core */}
                      <line x1={lineLeftX} y1={centerY} x2={centerX - MARRIAGE_DOT_R - 8} y2={centerY} stroke={highlightColor} strokeWidth={HIGHLIGHT_CORE_WIDTH} strokeLinecap="round" />
                      <text
                        x={centerX}
                        y={centerY + 6}
                        textAnchor="middle"
                        fontSize={22}
                        fontWeight="bold"
                        fill={highlightColor}
                      >
                        &#9829;
                      </text>
                      <line x1={centerX + MARRIAGE_DOT_R + 8} y1={centerY} x2={lineRightX} y2={centerY} stroke={highlightColor} strokeWidth={HIGHLIGHT_CORE_WIDTH} strokeLinecap="round" />
                    </g>
                  );
                })}
              </g>

              {/* expand/collapse buttons (SVG) + nodes */}
              <g>
                {Array.from(nodes.values()).map((node) => {
                  if (!shouldRenderNode(node)) return null;

                  const status = getNodeStatus(node);
                  const scaleFactor = status === 'selected' ? 1.5 : status === 'intermediate' ? 1.2 : 1;

                  // single node
                  if (node.type === "single") {
                    const p = node.persons[0];
                    if (!visiblePersonIds.has(p.id)) return null;
                    const c = getCoord(p.id);
                    const x = c.x - NODE_W / 2;
                    const y = c.y - NODE_H / 2;
                    const isMale = (p.gender || "").toLowerCase() === "male";
                    const bg = isMale ? ["#1167d1", "#0b67b3"] : ["#f06fa6", "#c34aa0"];
                    const clipId = `clip-${p.id}`;
                    const imageSrc = p.photoUrl || (isMale ? MaleImage : FemaleImage);
                    const nameLayout = computeNameLayout(p.name || "");
                    const AVATAR_SIZE = nameLayout.avatarSize;
                    const imageX = NODE_W / 2 - AVATAR_SIZE / 2;
                    const imageY = AVATAR_TOP;
                    const nameStartY = imageY + AVATAR_SIZE + 16;
                    const isSelf = isMyselfRecord(p);
                    const baseStrokeColor = getBorderColor(p);
                    const baseStrokeWidth = 4;

                    const centerX_rel = NODE_W / 2;
                    const centerY_rel = NODE_H / 2;
                    const nodeTransform = `translate(${centerX_rel}, ${centerY_rel}) scale(${scaleFactor}) translate(${-centerX_rel}, ${-centerY_rel})`;
                    const filter = status === 'other' ? 'url(#blur)' : '';

                    return (
                      <g key={p.id}>
                        {/* node card */}
                        <g
                          transform={`translate(${x}, ${y}) ${nodeTransform}`}
                          filter={filter}
                          className="node-card"
                          onPointerDown={(e) => handleNodePointerDown(e, p.id)}
                          style={{ cursor: nodeCursor, opacity: status === 'other' ? 0.4 : 1 }}
                        >
                          <defs>
                            <linearGradient id={`grad-${p.id}`} x1="0" x2="1">
                              <stop offset="0%" stopColor={bg[0]} />
                              <stop offset="100%" stopColor={bg[1]} />
                            </linearGradient>
                            <clipPath id={clipId}>
                                <circle cx={NODE_W / 2} cy={imageY + AVATAR_SIZE / 2} r={AVATAR_SIZE / 2} />
                              </clipPath>
                          </defs>
                          <rect
                            x={0}
                            y={0}
                            rx={12}
                            ry={12}
                            width={NODE_W}
                            height={NODE_H}
                            fill={`url(#grad-${p.id})`}
                            stroke={baseStrokeColor}
                            strokeWidth={baseStrokeWidth}
                            style={{ filter: "url(#soft)" }}
                          />
                          {isSelf && (
                            <rect
                              x={4}
                              y={4}
                              rx={10}
                              ry={10}
                              width={NODE_W - 8}
                              height={NODE_H - 8}
                              fill={`url(#${SELF_OVERLAY_GRADIENT_ID})`}
                              pointerEvents="none"
                            />
                          )}
                          {isSelf && (
                            <rect
                              x={8}
                              y={8}
                              rx={8}
                              ry={8}
                              width={NODE_W - 16}
                              height={NODE_H - 16}
                              fill={`url(#${SELF_FLARE_GRADIENT_ID})`}
                              pointerEvents="none"
                            />
                          )}
                          <image href={imageSrc} x={imageX} y={imageY} width={AVATAR_SIZE} height={AVATAR_SIZE} clipPath={`url(#${clipId})`} preserveAspectRatio="xMidYMid slice" />
                          <text x={NODE_W / 2} y={nameStartY} textAnchor="middle" style={{ fontSize: nameLayout.fontSize, fontWeight: 800, fill: "#fff" }}>
                            {nameLayout.lines.map((ln, idx) => (
                              <tspan key={idx} x={NODE_W / 2} dy={idx === 0 ? 0 : 14}>{ln}</tspan>
                            ))}
                          </text>
                          {isSelf && (
                            <g>
                              <rect
                                x={NODE_W - 60}
                                y={10}
                                width={48}
                                height={20}
                                rx={10}
                                fill={SELF_BADGE_BG}
                                opacity={0.95}
                              />
                              <text
                                x={NODE_W - 36}
                                y={24}
                                textAnchor="middle"
                                style={{ fontSize: 11, fontWeight: 700, fill: SELF_BADGE_TEXT_COLOR }}
                              >
                                {SELF_BADGE_LABEL}
                              </text>
                            </g>
                          )}
                        </g>
                      </g>
                    );
                  }

                  // couple node
                  const a = node.persons[0];
                  const b = node.persons[1];

                  // skip if neither spouse visible
                  if (!visiblePersonIds.has(a.id) && !visiblePersonIds.has(b.id)) return null;

                  const ca = getCoord(a.id);
                  const cb = getCoord(b.id);

                  const currentGap = NODE_W + COUPLE_GAP;
                  const requiredGap = NODE_W * scaleFactor;
                  const extraGap = scaleFactor > 1 ? 50 : 0;
                  const adjust = extraGap / 2;

                  const ax = (ca.x - adjust) - NODE_W / 2;
                  const ay = ca.y - NODE_H / 2;
                  const bx = (cb.x + adjust) - NODE_W / 2;
                  const by = cb.y - NODE_H / 2;
                  const ag = [(a.gender || "").toLowerCase() === "male" ? "#1167d1" : "#f06fa6", (a.gender || "").toLowerCase() === "male" ? "#0b67b3" : "#c34aa0"];
                  const bg = [(b.gender || "").toLowerCase() === "male" ? "#1167d1" : "#f06fa6", (b.gender || "").toLowerCase() === "male" ? "#0b67b3" : "#c34aa0"];
                  const aClipId = `clip-${a.id}`;
                  const bClipId = `clip-${b.id}`;
                  const aImageSrc = a.photoUrl || ((a.gender || "").toLowerCase() === "male" ? MaleImage : FemaleImage);
                  const bImageSrc = b.photoUrl || ((b.gender || "").toLowerCase() === "male" ? MaleImage : FemaleImage);
                  const aNameLayout = computeNameLayout(a.name || "");
                  const bNameLayout = computeNameLayout(b.name || "");
                  const aAvatar = aNameLayout.avatarSize;
                  const bAvatar = bNameLayout.avatarSize;
                  const aImageX = NODE_W / 2 - aAvatar / 2;
                  const bImageX = NODE_W / 2 - bAvatar / 2;
                  const imageY = AVATAR_TOP;
                  const aNameStartY = imageY + aAvatar + 16;
                  const bNameStartY = imageY + bAvatar + 16;
                  const isSelfA = isMyselfRecord(a);
                  const isSelfB = isMyselfRecord(b);
                  const aBaseStrokeColor = getBorderColor(a);
                  const bBaseStrokeColor = getBorderColor(b);
                  const baseStrokeWidth = 4;

                  const filter = status === 'other' ? 'url(#blur)' : '';

                  return (
                    <g key={node.id}>
                      {/* spouse A card */}
                      {visiblePersonIds.has(a.id) && (
                        <g
                          transform={`translate(${ax}, ${ay}) translate(${NODE_W / 2}, ${NODE_H / 2}) scale(${scaleFactor}) translate(${-NODE_W/2}, ${-NODE_H/2})`}
                          filter={filter}
                          className="node-card"
                          onPointerDown={(e) => handleNodePointerDown(e, a.id)}
                          style={{ cursor: nodeCursor, opacity: status === 'other' ? 0.4 : 1 }}
                        >
                          <defs>
                            <linearGradient id={`grad-${a.id}`} x1="0" x2="1">
                              <stop offset="0%" stopColor={ag[0]} />
                              <stop offset="100%" stopColor={ag[1]} />
                            </linearGradient>
                            <clipPath id={aClipId}>
                                <circle cx={NODE_W / 2} cy={imageY + aAvatar / 2} r={aAvatar / 2} />
                              </clipPath>
                          </defs>
                          <rect
                            x={0}
                            y={0}
                            rx={12}
                            ry={12}
                            width={NODE_W}
                            height={NODE_H}
                            fill={`url(#grad-${a.id})`}
                            stroke={aBaseStrokeColor}
                            strokeWidth={baseStrokeWidth}
                            style={{ filter: "url(#soft)" }}
                          />
                          {isSelfA && (
                            <rect
                              x={4}
                              y={4}
                              rx={10}
                              ry={10}
                              width={NODE_W - 8}
                              height={NODE_H - 8}
                              fill={`url(#${SELF_OVERLAY_GRADIENT_ID})`}
                              pointerEvents="none"
                            />
                          )}
                          {isSelfA && (
                            <rect
                              x={8}
                              y={8}
                              rx={8}
                              ry={8}
                              width={NODE_W - 16}
                              height={NODE_H - 16}
                              fill={`url(#${SELF_FLARE_GRADIENT_ID})`}
                              pointerEvents="none"
                            />
                          )}
                          <image href={aImageSrc} x={aImageX} y={imageY} width={aAvatar} height={aAvatar} clipPath={`url(#${aClipId})`} preserveAspectRatio="xMidYMid slice" />
                          <text x={NODE_W / 2} y={aNameStartY} textAnchor="middle" style={{ fontSize: aNameLayout.fontSize, fontWeight: 800, fill: "#fff" }}>
                            {aNameLayout.lines.map((ln, idx) => (
                              <tspan key={idx} x={NODE_W / 2} dy={idx === 0 ? 0 : 14}>{ln}</tspan>
                            ))}
                          </text>
                          {isSelfA && (
                            <g style={{ filter: `url(#${SELF_GLOW_FILTER_ID})` }}>
                              <rect
                                x={NODE_W - 60}
                                y={10}
                                width={48}
                                height={20}
                                rx={10}
                                fill={SELF_BADGE_BG}
                                opacity={0.95}
                              />
                              <text
                                x={NODE_W - 36}
                                y={24}
                                textAnchor="middle"
                                style={{ fontSize: 11, fontWeight: 700, fill: SELF_BADGE_TEXT_COLOR }}
                              >
                                {SELF_BADGE_LABEL}
                              </text>
                            </g>
                          )}
                        </g>
                      )}

                      {/* spouse B card */}
                      {visiblePersonIds.has(b.id) && (
                        <g
                          transform={`translate(${bx}, ${by}) translate(${NODE_W / 2}, ${NODE_H / 2}) scale(${scaleFactor}) translate(${-NODE_W/2}, ${-NODE_H/2})`}
                          filter={filter}
                          className="node-card"
                          onPointerDown={(e) => handleNodePointerDown(e, b.id)}
                          style={{ cursor: nodeCursor, opacity: status === 'other' ? 0.4 : 1 }}
                        >
                          <defs>
                            <linearGradient id={`grad-${b.id}`} x1="0" x2="1">
                              <stop offset="0%" stopColor={bg[0]} />
                              <stop offset="100%" stopColor={bg[1]} />
                            </linearGradient>
                            <clipPath id={bClipId}>
                                <circle cx={NODE_W / 2} cy={imageY + bAvatar / 2} r={bAvatar / 2} />
                              </clipPath>
                          </defs>
                          <rect
                            x={0}
                            y={0}
                            rx={12}
                            ry={12}
                            width={NODE_W}
                            height={NODE_H}
                            fill={`url(#grad-${b.id})`}
                            stroke={bBaseStrokeColor}
                            strokeWidth={baseStrokeWidth}
                            style={{ filter: "url(#soft)" }}
                          />
                          {isSelfB && (
                            <>
                              <rect
                                x={4}
                                y={4}
                                rx={10}
                                ry={10}
                                width={NODE_W - 8}
                                height={NODE_H - 8}
                                fill={`url(#${SELF_OVERLAY_GRADIENT_ID})`}
                                pointerEvents="none"
                              />
                              <rect
                                x={8}
                                y={8}
                                rx={8}
                                ry={8}
                                width={NODE_W - 16}
                                height={NODE_H - 16}
                                fill={`url(#${SELF_FLARE_GRADIENT_ID})`}
                                pointerEvents="none"
                              />
                            </>
                          )}
                          <image href={bImageSrc} x={bImageX} y={imageY} width={bAvatar} height={bAvatar} clipPath={`url(#${bClipId})`} preserveAspectRatio="xMidYMid slice" />
                          <text x={NODE_W / 2} y={bNameStartY} textAnchor="middle" style={{ fontSize: bNameLayout.fontSize, fontWeight: 800, fill: "#fff" }}>
                            {bNameLayout.lines.map((ln, idx) => (
                              <tspan key={idx} x={NODE_W / 2} dy={idx === 0 ? 0 : 14}>{ln}</tspan>
                            ))}
                          </text>
                          {isSelfB && (
                            <g style={{ filter: `url(#${SELF_GLOW_FILTER_ID})` }}>
                              <rect
                                x={NODE_W - 60}
                                y={10}
                                width={48}
                                height={20}
                                rx={10}
                                fill={SELF_BADGE_BG}
                                opacity={0.95}
                              />
                              <text
                                x={NODE_W - 36}
                                y={24}
                                textAnchor="middle"
                                style={{ fontSize: 11, fontWeight: 700, fill: SELF_BADGE_TEXT_COLOR }}
                              >
                                {SELF_BADGE_LABEL}
                              </text>
                            </g>
                          )}
                        </g>
                      )}
                    </g>
                  );
                })}
              </g>
            </g>
          </svg>
        </MDBox>

        {/* Person Popup */}
        {selectedPersonPopup && (
         <Card sx={{
                     position: "fixed",
                     top: stickyTop + 8,
                     right: 24,
                     left: "unset",
                     width: 320,
                     zIndex: 2000,
                     boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
                     borderRadius: 3,
                     overflow: "hidden",
                     maxHeight: "80vh",
                     overflowY: "auto",
                     mt: 0.5
                   }}>
            <MDBox p={1.5}>
              <MDBox display="flex" flexDirection="column" alignItems="center" mb={1}>
                <MDBox
                  component="img"
                  src={selectedPersonPopup.photoUrl || ((selectedPersonPopup.gender || "").toLowerCase() === "male" ? MaleImage : FemaleImage)}
                  alt={selectedPersonPopup.name}
                  sx={{
                    width: 100,
                    height: 100,
                    borderRadius: "50%",
                    mb: 0.5,
                    border: `4px solid ${getBorderColor(selectedPersonPopup)}`,
                    boxShadow: "0 4px 8px rgba(0,0,0,0.2)"
                  }}
                />
                <MDTypography variant="h5" fontWeight="bold" color="dark" textAlign="center" sx={{ fontSize: "1.5rem" }}>
                  {selectedPersonPopup.name}
                </MDTypography>
   
              </MDBox>

              <MDBox mb={0}>
                           <MDTypography variant="caption" color="text"  fontWeight="medium">
                  Gender: {selectedPersonPopup.gender || "Unknown"}
                </MDTypography>
              </MDBox>
              <MDBox mb={0}>
                <MDTypography variant="caption" color="text" fontWeight="medium">
                  Age: {selectedPersonPopup.birthdate ? Math.floor((new Date() - new Date(selectedPersonPopup.birthdate)) / (365.25 * 24 * 60 * 60 * 1000)) : "N/A"}
                </MDTypography>
              </MDBox>

              <MDBox mb={0}>
                <MDTypography variant="caption" color="text" fontWeight="medium">
                  Email: {selectedPersonPopup.email || "N/A"}
                </MDTypography>
              </MDBox>

              <MDBox mb={0}>
                <MDTypography variant="caption" color="text" fontWeight="medium">
                  Phone: {selectedPersonPopup.phone || "N/A"}
                </MDTypography>
              </MDBox>

              <MDBox mb={0}>
                <MDTypography variant="caption" color="text" fontWeight="medium">
                  Marital Status: {selectedSpouse ? "Married" : "Single"}
                </MDTypography>
              </MDBox>

              {selectedSpouse && (
                <MDBox mb={0}>
                  <MDTypography variant="caption" color="text" fontWeight="medium">
                    Spouse: {selectedSpouse.name || "Unknown"}
                  </MDTypography>
                </MDBox>
              )}

              <MDBox mb={0}>
                <MDTypography variant="caption" color="text" fontWeight="medium">
                  Children:
                </MDTypography>
                {(() => {
                  const parentIds = new Set();
                  if (selectedPersonPopup?.id) parentIds.add(selectedPersonPopup.id);
                  if (selectedSpouse?.id) parentIds.add(selectedSpouse.id);
                  const allChildren = persons.filter((p) => p.parentId && parentIds.has(p.parentId));
                  return allChildren.length > 0 ? (
                    <MDBox component="ul" sx={{ pl: 2, m: 0 }}>
                      {allChildren.map(child => (
                        <MDBox component="li" key={child.id} sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                          {child.name} ({child.gender || "Unknown"})
                        </MDBox>
                      ))}
                    </MDBox>
                  ) : (
                    <MDTypography variant="caption" color="text" sx={{ fontStyle: "italic" }}>
                      No children
                    </MDTypography>
                  );
                })()}
              </MDBox>

              <MDBox display="flex" justifyContent="flex-end" mt={1}>
                <MDButton
                  variant="outlined"
                  color="dark"
                  size="small"
                  onClick={() => setSelectedPersonPopup(null)}
                  sx={{ minWidth: 80 }}
                >
                  Close
                </MDButton>
              </MDBox>
            </MDBox>
          </Card>
        )}
      </Card>

      </div>

      <ExistingMemberLinkDialog
        open={linkDialogOpen}
        onClose={() => setLinkDialogOpen(false)}
        members={persons}
        onLinkSuccess={onLinkSuccess}
      />

      <RelationshipChecker
        members={persons}
        isOpen={drawerOpen}
        onClose={handleToggleRelationship}
        onNodeClick={(handler) => { relationshipClickHandlerRef.current = handler; }}
        onPairSelected={handlePairSelected}
        currentPath={currentRelationshipPath}
      />
    </>
  );
}
